import { type Popover } from 'bootstrap';
import { on } from 'delegated-events';
import { observe } from 'selector-observer';

import { focusFirstFocusableChild, onDocumentReady, trapFocus } from '@prairielearn/browser-utils';

import { getPopoverContainerForTrigger, getPopoverTriggerForContainer } from '../lib/popover.js';

const openPopovers = new Set<Popover>();

function closeOpenPopovers() {
  openPopovers.forEach((popover) => popover.hide());
  openPopovers.clear();
}

/**
 * Returns the trigger modes for a popover trigger as configured by `data-bs-trigger`,
 * `data-trigger`, or `trigger: ...` in the popover's options.
 *
 * If the trigger mode cannot be determined, an empty array is returned.
 */
function getPopoverTriggerModes(trigger: HTMLElement): string[] {
  const instance = window.bootstrap.Popover.getInstance(trigger);
  if (instance) {
    return (instance as any)._config.trigger.split(' ') ?? [];
  }

  return [];
}

/**
 * Returns whether a popover trigger is focus-triggered.
 */
function isFocusTrigger(trigger: HTMLElement) {
  const triggerModes = getPopoverTriggerModes(trigger);
  return triggerModes.includes('focus');
}

// We need to wrap this in `onDocumentReady` because Bootstrap doesn't
// add its jQuery API to the jQuery object until after this event.
// `selector-observer` will start running its callbacks immediately, so they'd
// otherwise execute too soon.
onDocumentReady(() => {
  observe('[data-bs-toggle="popover"]', {
    constructor: HTMLElement,
    add(el) {
      new window.bootstrap.Popover(el, { sanitize: false });

      if (getPopoverTriggerModes(el).includes('click')) {
        el.setAttribute('aria-haspopup', 'dialog');
        el.setAttribute('aria-expanded', 'false');
      }

      // Bootstrap will by default copy the `title` attribute to `aria-label`,
      // but it won't do that for `data-bs-title`. We do that here in the interest
      // of making things maximally accessible by default. If an `aria-label`
      // attribute is already present, we leave it alone.
      if (!el.hasAttribute('aria-label')) {
        const title = el.dataset.bsTitle;
        if (title && !el.textContent.trim()) {
          el.setAttribute('aria-label', title);
        }
      }
    },
    remove(el) {
      window.bootstrap.Popover.getInstance(el)?.dispose();
    },
  });

  // Bootstrap supports `data-bs-dismiss="alert"` and `data-bs-dismiss="modal"`, but not
  // `data-bs-dismiss="popover"`. This behavior adds support for dismissing popovers
  // in such a declarative way.
  on('click', '[data-bs-dismiss="popover"]', (event) => {
    const popoverContainer = event.currentTarget.closest('.popover');
    if (!popoverContainer || !(popoverContainer instanceof HTMLElement)) return;

    const trigger = getPopoverTriggerForContainer(popoverContainer);
    if (!trigger) return;

    window.bootstrap.Popover.getInstance(trigger)?.hide();
  });

  // Close open popovers if the user hits the escape key.
  //
  // Note that this does not gracefully handle popovers inside of modals, as
  // Bootstrap has its own escape key handling for modals.
  on('keydown', 'body', (event) => {
    if (event.key === 'Escape') {
      closeOpenPopovers();
    }
  });

  on('click', 'body', (e) => {
    if (openPopovers.size === 0) return;

    // If this click occurred inside a popover, do nothing.
    const closestPopover = (e.target as HTMLElement).closest('.popover');
    if (closestPopover) return;

    // Close all open popovers.
    closeOpenPopovers();
  });

  // Hide other open popovers when a new popover is opened.
  on('show.bs.popover', 'body', () => {
    closeOpenPopovers();
  });

  on('inserted.bs.popover', 'body', (event) => {
    const target = event.target as HTMLElement;
    const container = getPopoverContainerForTrigger(target);

    if (container && getPopoverTriggerModes(target).includes('click')) {
      const header = container.querySelector<HTMLElement>('.popover-header');
      const title = header?.textContent.trim();
      const headerContainer = document.createElement('div');
      headerContainer.className = 'popover-header d-flex align-items-center gap-2';

      if (header && title) {
        const heading = document.createElement('h3');
        heading.className = 'h6 mb-0 flex-grow-1';
        heading.id = `${container.id}-title`;
        heading.append(...header.childNodes);
        headerContainer.append(heading);
        header.replaceWith(headerContainer);
        container.setAttribute('aria-labelledby', heading.id);
      } else {
        header?.replaceWith(headerContainer);
        if (!header) container.querySelector('.popover-body')?.before(headerContainer);
        container.setAttribute(
          'aria-label',
          target.getAttribute('aria-label') || target.textContent.trim() || 'Popover',
        );
      }

      container.setAttribute('role', 'dialog');

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'btn-close ms-auto flex-shrink-0';
      closeButton.setAttribute('aria-label', 'Close');
      closeButton.setAttribute('data-bs-dismiss', 'popover');
      headerContainer.append(closeButton);
    }

    // If MathJax is loaded on this page, attempt to typeset any math
    // that may be in the popover.
    if (container && window.MathJax !== undefined) {
      window.MathJax.typesetPromise([container]);
    }
  });

  on('shown.bs.popover', 'body', (event) => {
    const target = event.target as HTMLElement;

    if (target.getAttribute('aria-haspopup') === 'dialog') {
      target.setAttribute('aria-expanded', 'true');
    }

    const popover = window.bootstrap.Popover.getInstance(target);
    if (popover) openPopovers.add(popover);

    const container = getPopoverContainerForTrigger(target);

    // If the popover is focus-triggered, we'll skip the focus trap and
    // autofocus logic. If we move the focus off the trigger, the popover
    // will immediately close, which we don't want.
    if (container && !isFocusTrigger(target)) {
      // Trap focus inside this new popover.
      const trap = trapFocus(container);

      // Remove focus trap when this popover is ultimately hidden.
      const removeFocusTrap = () => {
        trap.deactivate();
        target.removeEventListener('hide.bs.popover', removeFocusTrap);
      };
      target.addEventListener('hide.bs.popover', removeFocusTrap);

      // Attempt to place focus on the correct item inside the popover.
      const popoverBody = container.querySelector<HTMLElement>('.popover-body')!;
      focusFirstFocusableChild(popoverBody);
      if (!container.contains(document.activeElement)) {
        container.querySelector<HTMLButtonElement>('.btn-close')?.focus();
      }
    }
  });

  on('hide.bs.popover', 'body', (event) => {
    const target = event.target as HTMLElement;
    if (target.getAttribute('aria-haspopup') === 'dialog') {
      target.setAttribute('aria-expanded', 'false');
    }

    const popover = window.bootstrap.Popover.getInstance(target);
    if (popover) openPopovers.delete(popover);
  });
});
